import { ComponentType } from 'react';
import { ViewerRegistration, IViewerRegistry, ViewerComponentProps } from './types';

/**
 * Singleton registry for file viewer components
 * Allows dynamic registration of viewers for different file types
 */
class ViewerRegistry implements IViewerRegistry {
  private viewers: ViewerRegistration[] = [];

  /**
   * Register a new viewer
   * Viewers are sorted by priority (higher priority first)
   */
  register(registration: Omit<ViewerRegistration, 'priority'> & { priority?: number }): void {
    const fullRegistration: ViewerRegistration = {
      ...registration,
      priority: registration.priority ?? 0,
    };

    // Check if viewer with same ID already exists
    const existingIndex = this.viewers.findIndex(v => v.id === fullRegistration.id);

    if (existingIndex !== -1) {
      // Replace existing viewer
      this.viewers[existingIndex] = fullRegistration;
    } else {
      // Add new viewer
      this.viewers.push(fullRegistration);
    }

    // Sort by priority (descending)
    this.viewers.sort((a, b) => b.priority - a.priority);
  }

  /**
   * Unregister a viewer by ID
   */
  unregister(id: string): void {
    this.viewers = this.viewers.filter(v => v.id !== id);
  }

  /**
   * Get the first matching viewer component for a file
   * Returns null if no viewer supports the file
   */
  getViewer(url: string, fileType: string | null): ComponentType<ViewerComponentProps> | null {
    const viewer = this.viewers.find(v => v.matcher(url, fileType));
    return viewer?.component ?? null;
  }

  /**
   * Check if any registered viewer supports this file
   */
  isSupported(url: string, fileType: string | null): boolean {
    return this.viewers.some(v => v.matcher(url, fileType));
  }

  /**
   * Get all registered viewers (for debugging)
   */
  getAll(): ViewerRegistration[] {
    return [...this.viewers];
  }

  /**
   * Clear all registered viewers
   */
  clear(): void {
    this.viewers = [];
  }
}

// Export singleton instance
export const viewerRegistry = new ViewerRegistry();
export default viewerRegistry;
